import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Validate Exams';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.BulkCandidateTools,
	portal: 'SP',
};

const htmlContent = `
<p><strong>To create the Comma Separated File (CSV) import file: </strong></p>
<p>
	Include the following column headers recognized by the parser. All fields are required for a
	successful upload. Extra fields will be ignored. Omitting the required columns will result
	in an error.
</p>
<ul>
	<li>Exam Registration Number</li>
	<li>Exam State</li>
	<li>Note</li>
</ul>
<p>Supported entries for Exam State include:</p>
<ul>
		<li>Invalid</li>
		<li>Ok</li>
</ul>
<p>
	Below the column header lines, add a line for each exam registration ID requiring a grade
	update and/or valid exam state.
</p>
<p><strong>Return CSV file: </strong></p>
<p>After pressing the Submit button, a CSV file will be returned with following columns:</p>
<ul>
	<li>Exam Registration Number</li>
	<li>Delivery Provider Name</li>
	<li>Exam State</li>
	<li>Note</li>
	<li>Update Status</li>
</ul>
<p>The status field will contain one of these values:</p>
<ul>
	<li>
		Duplicate Records - The same exam registration number appears multiple times in the
		upload file with conflicting data, please review.
	</li>
	<li>
		Not Found - The data provided does not match any record in our system. This can indicate
		that the exam registration number was not found or the registration number was found but
		the delivery provider name does not match.
	</li>
	<li>Success - The state was successfully updated for the exam registration number.</li>
</ul>
`;

export type SPExamUpdateValidationLocNames =
	| 'SPExamUpdateValidationPageTitle'
	| 'SPExamUpdateValidationPageHeading'
	| 'SPExamUpdateValidationButton'
	| 'SPExamUpdateValidationInstructions'
	| 'SPExamUpdateValidationDataProviderLabel'
	| 'SPExamUpdateValidationDataProviderRequired'
	| 'SPExamUpdateValidationMaxRowsError'
	| 'SPExamUpdateValidationCurrentImports'
	| 'SPExamUpdateValidationStatusProcessing'
	| 'SPExamUpdateValidationStatusCompleted'
	| 'SPExamUpdateValidationStatusFailed'
	| 'SPExamUpdateValidationUploadQueuedSuccess'
	| 'SPExamUpdateValidationNoResults'
	| 'SPExamUpdateValidationDownloadError'
	| 'SPExamUpdateValidationDownloadTooltip'
	| 'SPExamUpdateValidationColumnFileName'
	| 'SPExamUpdateValidationColumnStatus'
	| 'SPExamUpdateValidationColumnDate';

export const spExamUpdateValidationLocs: LocDefs<SPExamUpdateValidationLocNames> = {
	SPExamUpdateValidationPageTitle: {
		defaultValue: 'Update Exam Validation',
		description: 'Page title for the exam update validation page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamUpdateValidationPageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page Heading for the exam update validation page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamUpdateValidationInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the exam update validation page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPExamUpdateValidationButton: {
		defaultValue: 'Update Exams',
		description: 'Button to start updating validation for exams',
		displayPath: `${dpRoot}|Corrupt Exams Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPExamUpdateValidationDataProviderLabel: {
		defaultValue: 'Delivery Provider',
		description: 'Delivery Provider label for the exam corruption page',
		displayPath: `${dpRoot}|Delivery Provider`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationDataProviderRequired: {
		defaultValue: 'Delivery Provider is required.',
		description: 'Error shown when delivery provider is not selected',
		displayPath: `${dpRoot}|Delivery Provider Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamUpdateValidationMaxRowsError: {
		defaultValue: 'You can only update 500 exams at a time.',
		description: 'Error shown when the number of rows exceeds the limit',
		displayPath: `${dpRoot}|Max Rows Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamUpdateValidationCurrentImports: {
		defaultValue: 'Current Imports',
		description: 'Title for the current imports section',
		displayPath: `${dpRoot}|Current Imports`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamUpdateValidationStatusProcessing: {
		defaultValue: 'Processing',
		description: 'Status label for processing jobs',
		displayPath: `${dpRoot}|Status Processing`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationStatusCompleted: {
		defaultValue: 'Completed',
		description: 'Status label for completed jobs',
		displayPath: `${dpRoot}|Status Completed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationStatusFailed: {
		defaultValue: 'Failed',
		description: 'Status label for failed jobs',
		displayPath: `${dpRoot}|Status Failed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationUploadQueuedSuccess: {
		defaultValue: 'Upload queued successfully',
		description: 'Success message when upload is queued',
		displayPath: `${dpRoot}|Upload Queued Success`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPExamUpdateValidationNoResults: {
		defaultValue: 'No results available for download',
		description: 'Error message when no results are available',
		displayPath: `${dpRoot}|No Results`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamUpdateValidationDownloadError: {
		defaultValue: 'Failed to download results',
		description: 'Error message when download fails',
		displayPath: `${dpRoot}|Download Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamUpdateValidationDownloadTooltip: {
		defaultValue: 'Download Results',
		description: 'Tooltip for download button',
		displayPath: `${dpRoot}|Download Tooltip`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPExamUpdateValidationColumnFileName: {
		defaultValue: 'File Name',
		description: 'Column header for file name',
		displayPath: `${dpRoot}|Column File Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationColumnStatus: {
		defaultValue: 'Status',
		description: 'Column header for status',
		displayPath: `${dpRoot}|Column Status`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamUpdateValidationColumnDate: {
		defaultValue: 'Date',
		description: 'Column header for date',
		displayPath: `${dpRoot}|Column Date`,
		displayType: 'Label',
		...sharedProperties,
	},
};

export const EXAM_VALIDATION_LOC_KEYS = MapLocKeys<SPExamUpdateValidationLocNames>(
	spExamUpdateValidationLocs
);
export type SPExamUpdateValidationLocType = LocMap<SPExamUpdateValidationLocNames>;
